import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { TeacherStat } from "@/hooks/useClassTrialStats";
import { getTrialStatusColor } from "@/components/admin/class-trials/classTrialStatusColors";

interface Props {
  teacherStats: TeacherStat[];
}

type SortColumn = "total" | "success" | "failure" | "refund" | "inProgress" | "conversionRate";
type SortDir = "asc" | "desc";

function rateColor(rate: number) {
  if (rate >= 50) return "text-green-600 font-bold";
  if (rate >= 30) return "text-yellow-600 font-semibold";
  return "text-red-600";
}

function SortButton({ active, dir, onClick }: { col: SortColumn; active: boolean; dir: SortDir; onClick: () => void }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-0.5 hover:text-foreground transition-colors">
      {active ? (
        dir === "desc" ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
      ) : (
        <ChevronDown className="h-3 w-3 opacity-30" />
      )}
    </button>
  );
}

export function TeacherStatsTable({ teacherStats }: Props) {
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [sortCol, setSortCol] = useState<SortColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const toggle = (code: string) => {
    setOpenRows((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const handleSort = (col: SortColumn) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  const sorted = useMemo(() => {
    if (!sortCol) return teacherStats;
    return [...teacherStats].sort((a, b) => {
      const av = a[sortCol] as number;
      const bv = b[sortCol] as number;
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [teacherStats, sortCol, sortDir]);

  const th = (label: string, col: SortColumn) => (
    <th className="text-right p-2">
      <span className="inline-flex items-center justify-end gap-1">
        {label}
        <SortButton col={col} active={sortCol === col} dir={sortDir} onClick={() => handleSort(col)} />
      </span>
    </th>
  );

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">สถิติรายครู</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-2 w-8"></th>
                <th className="text-left p-2">ครู</th>
                {th("ทั้งหมด", "total")}
                {th("สำเร็จ", "success")}
                {th("ไม่สำเร็จ", "failure")}
                {th("คืนเงิน", "refund")}
                {th("ดำเนินการ", "inProgress")}
                {th("อัตรา%", "conversionRate")}
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <Collapsible key={t.teacherCode} open={openRows[t.teacherCode]} onOpenChange={() => toggle(t.teacherCode)} asChild>
                  <>
                    <CollapsibleTrigger asChild>
                      <tr className="border-b cursor-pointer hover:bg-muted/30">
                        <td className="p-2">
                          {openRows[t.teacherCode] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="p-2 font-medium">{`${t.teacherName} (${t.teacherNickname})`}<br /><span className="text-xs text-muted-foreground">{t.teacherCode}</span></td>
                        <td className="p-2 text-right">{t.total}</td>
                        <td className="p-2 text-right text-green-600">{t.success}</td>
                        <td className="p-2 text-right text-red-600">{t.failure}</td>
                        <td className="p-2 text-right text-purple-600">{t.refund}</td>
                        <td className="p-2 text-right text-amber-600">{t.inProgress}</td>
                        <td className={`p-2 text-right ${rateColor(t.conversionRate)}`}>{t.conversionRate.toFixed(1)}%</td>
                      </tr>
                    </CollapsibleTrigger>
                    <CollapsibleContent asChild>
                      <tr>
                        <td colSpan={8} className="p-0">
                          <div className="bg-muted/20 p-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left p-1">ชื่อเล่น</th>
                                  <th className="text-left p-1">ชื่อ</th>
                                  <th className="text-left p-1">ระดับชั้น</th>
                                  <th className="text-left p-1">สาขา</th>
                                  <th className="text-left p-1">วันทดลอง</th>
                                  <th className="text-left p-1">สถานะ</th>
                                </tr>
                              </thead>
                              <tbody>
                                {t.trials.map((tr) => (
                                  <tr key={tr.id} className="border-b last:border-0">
                                    <td className="p-1">{tr.student_nickname || "-"}</td>
                                    <td className="p-1">{tr.student_name || `${tr.student_firstname || ""} ${tr.student_lastname || ""}`.trim() || "-"}</td>
                                    <td className="p-1">{tr.grade || "-"}</td>
                                    <td className="p-1">{tr.location || "-"}</td>
                                    <td className="p-1">{tr.class_date || "-"}</td>
                                    <td className="p-1">
                                      <Badge variant="outline" className={`text-xs ${getTrialStatusColor(tr.status || "")}`}>
                                        {tr.status || "-"}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    </CollapsibleContent>
                  </>
                </Collapsible>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
